import { Chart, ChartTitle, ChartLegend, ChartSeries, ChartSeriesItem, ChartSeriesLabels, ChartArea, SeriesClickEvent, ChartCategoryAxis, ChartCategoryAxisItem, ChartCategoryAxisTitle, ChartValueAxis, ChartValueAxisItem, ChartValueAxisTitle } from "@progress/kendo-react-charts"
import axios from "axios";
import { useEffect, useState } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { useLocalization } from "@progress/kendo-react-intl";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { AccessToken } from "../config/AccessToken";
import 'hammerjs';
import { Checkbox } from "@progress/kendo-react-inputs";
import { loadingPanel } from "../components/LoadingPanel";

/**
 * Component Areas renders a pie chart with the number of areas and the number of areas with devices.
 * When the user clicks on a pie chart slice, the list of devices for that zone is displayed in a grid.
 * The user can select an zone from a dropdown list and the list of devices for that zone is displayed in a grid.
 * The user can hide areas with no devices from the pie chart by clicking on a checkbox.
 * @returns {JSX.Element}
 */
export const Areas = () => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  const [zonesInfo, setZonesInfo] = useState([])
  const [count, setCount] = useState(0)
  const [countNoZone, setCountNoZone] = useState(0)
  const [countWithZone, setCountWithZone] = useState(0)
  const [zoneName, setZoneName] = useState([])

  const [zone, setZone] = useState('');
  const [showGrid, setShowGrid] = useState(false);
  const [devicesInArea, setDevicesInArea] = useState([]);
  const [countDevicesInAreas, setCountDevicesInAreas] = useState(0);

  const localizationService = useLocalization();

  const [instance, accessTokenRequest] = AccessToken();

  const [listIdNamesZone, setListIdNamesZone] = useState<any>([])
  const [listIdNamesZoneWithDevices, setListIdNamesZoneWithDevices] = useState<any>([])
  const [listIdNamesZoneNoDevices, setListIdNamesZoneNoDevices] = useState<any>([])
  const [listZoneWithDevices, setListZoneWithDevices] = useState<any>([])
  const [listZoneNoDevices, setListZoneNoDevices] = useState<any>([])

  const [devicesOutOfZone, setDevicesOutOfZone] = useState<any>([])

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getZoneList()
  }, [])

  const getZoneList = async () => {

    const commonOperations = (accessToken: any) => {
      setLoading(true)
      let languageId = localStorage.getItem('languageId');
      const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
      axios.get(`${SERVER_URL}/zone/wakeup/list`, { headers })
        .then((res: any) => {

          if (res.length == 0) return;

          const data = res.data.data
          setCount(data.length)
          setZonesInfo(data)
          let listZone: any = []
          let listZoneWithDevices: any = []
          let listZoneNoDevices: any = []
          let countNoZone: number = 0
          let countWithZone: number = 0
          data.forEach((zone: any) => {
            listZone.push(zone.name)
            listIdNamesZone.push({ category: zone.name, color: zone.color, value: zone.wakeupList.length })
            if (zone.wakeupList.length == 0) {
              countNoZone++
              listZoneNoDevices.push(zone.name)
              listIdNamesZoneNoDevices.push({ category: zone.name, color: zone.color, value: zone.wakeupList.length })
            }
            else {
              countWithZone++
              listZoneWithDevices.push(zone.name)
              listIdNamesZoneWithDevices.push({ category: zone.name, color: zone.color, value: zone.wakeupList.length })
            }
            setCountNoZone(countNoZone)
            setCountWithZone(countWithZone)

            // Ordina per 'value' in ordine crescente
            listIdNamesZoneWithDevices.sort((a: any, b: any) => {
              return a.value - b.value;
            });
            setListIdNamesZoneWithDevices(listIdNamesZoneWithDevices)

            setListIdNamesZoneNoDevices(listIdNamesZoneNoDevices)
          });
          
          listIdNamesZone.sort(function (a: any, b: any) {
            return a.value > b.value ? 1 : -1;
          });
          setListIdNamesZone(listIdNamesZone)

          setZoneName(listZone)
          setListZoneNoDevices(listZoneNoDevices)
          setListZoneWithDevices(listZoneWithDevices)

          setLoading(false)
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
          setLoading(false)
        });
    }
    if (localStorage.getItem('accessToken') != null) {
      let accessToken = localStorage.getItem('accessToken');
      commonOperations(accessToken);
    } else {
      await instance.initialize();
      await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
        let accessToken = accessTokenResponse.accessToken;
        commonOperations(accessToken);
      })
        .catch((error: any) => {
          if (error instanceof InteractionRequiredAuthError) {
            instance.acquireTokenRedirect(accessTokenRequest);
          }
          console.log(error);
        });
    }
  }

  useEffect(() => {
    const getDeviceList = async (onlyBound?: boolean) => {
      let languageId = localStorage.getItem('languageId');

      setLoading(true)

      const commonOperations = async (accessToken: any) => {
        const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

        await axios.get(`${SERVER_URL}/device/list`, { headers }).then((res) => {
          const data = res?.data

          let devicesOutOfZone: any = []

          // format date in data.data
          data?.data?.forEach((device: any) => {
            if (device?.zoneNameListJoined == null) {
              devicesOutOfZone.push(device)
            }
          })
          setDevicesOutOfZone(devicesOutOfZone)

          setLoading(false)
        })
          .catch((error) => {
            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }
            setLoading(false)
          });
      }

      if (localStorage.getItem('accessToken') != null) {
        let accessToken = localStorage.getItem('accessToken');
        commonOperations(accessToken);
      } else {
        await instance.initialize();
        await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
          // Acquire token silent success
          let accessToken = accessTokenResponse.accessToken;
          commonOperations(accessToken);
        })
      }
    }

    getDeviceList()
  }, [])

  const labelContent = (props: any) => {
    let infoArea: any;
    return infoArea = `${props.category}: ${props.value}`
  };

  const handleChangeArea = (event: any, clickFromChart?: boolean) => {

    setLoading(true)

    let nameArea: any = '';
    if (!clickFromChart) {
      nameArea = event?.target?.value;
    } else {
      nameArea = event?.category;
    }

    setZone(nameArea);
    let listDevicesInArea: any = []
    let listDevicesWithoutArea: any = []
    zonesInfo.forEach((zone: any) => {
      if (zone.name == nameArea) {
        zone?.wakeupList.forEach((tag: any) => {
          if (zone.id == tag.zoneId) listDevicesInArea.push({ 'deviceMac': tag.deviceMac, 'deviceName': tag.deviceName })
        });
      } else {
        zone?.wakeupList.forEach((tag: any) => {
          if (zone.id == tag.zoneId) listDevicesWithoutArea.push({ 'deviceMac': tag.deviceMac, 'deviceName': tag.deviceName })
        });
      }
    });
    //onToggleZoneDevices ? setDevicesInArea(listDevicesWithoutArea) : setDevicesInArea(listDevicesInArea)
    setDevicesInArea(listDevicesInArea)
    setCountDevicesInAreas(listDevicesInArea.length);
    setShowGrid(true);

    if (!clickFromChart) {
      onClickChart(event, true);
    }
    setLoading(false)
  };

  const onClickChart = (event: SeriesClickEvent, clickFromTable?: boolean) => {

    setLoading(true)

    let nameArea: any = '';
    if (!clickFromTable) {
      nameArea = event?.category;
    } else {
      nameArea = event?.value;
    }

    let newData: any = []
    if (!onToggleZoneDevices) {
      newData = listIdNamesZone.map((item: any) => {
        item.exploded = item.category === nameArea ? !item.exploded : false;
        return item;
      });
    }
    else {
      newData = listIdNamesZoneWithDevices.map((item: any) => {
        item.exploded = item.category === nameArea ? !item.exploded : false;
        return item;
      });
    }

    if (!clickFromTable) {
      handleChangeArea(event, true);
    }
    setLoading(false)
  }

  const [onToggleZoneDevices, setOnToggleZoneDevices] = useState(true);

  const onToggleZoneDevicesClick = () => {
    setOnToggleZoneDevices(!onToggleZoneDevices);
    setZone('');
    setShowGrid(false);
  }

  return (
    <>
      {loading && loadingPanel}
      {listIdNamesZone.length > 0 ?
        <div className="flex-parent-element">
          <div className="flex-left-child-element" style={{ position: "relative", flex: '0 0 65%' }}>
            <Chart
              style={{ height: '100%' }}
              onSeriesClick={(e: SeriesClickEvent) => onClickChart(e)}
            >
              <ChartTitle
                text={`${localizationService.toLanguageString('zone.number_of_zones', 'NOT_FOUND')}: ${count}  -  ${localizationService.toLanguageString('zone.zones_with_devices', 'NOT_FOUND')}: ${countWithZone}  -  ${localizationService.toLanguageString('zone.zones_with_no_devices', 'NOT_FOUND')}: ${countNoZone}`}
                color="#018CA5"
                margin={{ bottom: 50 }}
              />
              <ChartLegend
                position="bottom"
                reverse={false}
                labels={{ color: "#018CA5" }}
              />
              <ChartArea background="#F7F7F7" margin={10} />
              <ChartSeries>
                <ChartSeriesItem
                  type="donut"
                  //overlay={{ gradient: "sharpBevel" }}
                  data={onToggleZoneDevices ? listIdNamesZoneWithDevices : listIdNamesZone}
                  field="value"
                  categoryField="category"
                  explodeField="exploded"
                  colorField="color"
                  tooltip={{ visible: true }}
                //connectors={{ width: 0 }}
                //line={{ color: "red" }}
                >
                  {/*<ChartSeriesLabels
                    color="#018CA5"
                    background="none"
                    content={labelContent}
                    font="bold"
                  //position="outsideEnd"
                  //align="circle"
                  />*/}
                </ChartSeriesItem>
              </ChartSeries>
            </Chart>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -40%)",
                textAlign: "center",
                pointerEvents: "none",
                fontSize: '2vh'
              }}
            >
              <div style={{ fontSize: '3vh', fontWeight: "bold" }}>{count}</div>
              <div style={{ fontSize: '2vh' }}>Total Zones</div>
            </div>
          </div>

          <div className="flex-right-child-element-areas">
            <h1 className="k-h4" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('zone.zones_list', 'NOT_FOUND')}</h1>
            <DropDownList
              //data={onToggleZoneDevices ? listZoneWithDevices : zoneName}
              data={onToggleZoneDevices ? listZoneWithDevices : zoneName}
              value={zone}
              defaultValue={localizationService.toLanguageString('zone.select_zone', 'NOT_FOUND')}
              onChange={handleChangeArea}
              style={{ color: '#018CA5', fontSize: '2vh' }}
              title={localizationService.toLanguageString('zone.select_zone', 'NOT_FOUND')}
            />
            {showGrid &&
              <>
                <h4 className="k-h6" style={{ color: '#018CA5' }}>{localizationService.toLanguageString('zone.device_list', 'NOT_FOUND')}</h4>
                <Grid
                  style={{
                    height: "400px",
                    padding: '0px'
                  }}
                  data={devicesInArea}
                  scrollable={'none'}
                // pageable={{ type: 'numeric', buttonCount: 1, info:true}}  
                >
                  <GridColumn
                    field="deviceMac"
                    title="Mac"
                    width="100%"
                  />
                  <GridColumn
                    field="deviceName"
                    title={localizationService.toLanguageString('zone.name', 'NOT_FOUND')}
                    width="100%"
                  />
                </Grid>
              </>
            }
            <div className="flex-left-child-element">
              <br />
              <div style={{ display: 'inline-flex', verticalAlign: 'middle', fontSize: '2vh' }}>
                <Checkbox
                  label={localizationService.toLanguageString('zone.hide_zone_no_devices', 'NOT_FOUND')}
                  value={onToggleZoneDevices}
                  onChange={onToggleZoneDevicesClick}
                />
              </div>
            </div>
          </div>
        </div>
        :
        // Mostra un messaggio o un altro contenuto se non ci sono dati
        <div style={{ textAlign: 'center', padding: '20px', border: '1px dashed #ccc' }}>
          <p>{localizationService.toLanguageString('zone.no_value', 'NOT_FOUND')}</p>
        </div>
      }
    </>
  )
}